import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LogoutIcon from '../svg/logout.svg';

// Reusable Floating Navigation Bar Component
// Use this component for bottom-centered floating navbars with glassmorphism effect
export const FloatingNavBar = ({ 
  tabs = [], 
  activeTab, 
  onTabChange,
  showLogout = false,
  className = ''
}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    try {
      console.log('🔓 Logging out user...');
      await logout();
      console.log('✅ Logout successful, redirecting to login...');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('❌ Logout failed:', error);
      navigate('/login', { replace: true });
    }
  };

  const navStyle = {
    background: 'linear-gradient(243deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(90deg, rgba(36, 94, 255, 0.10) 0%, rgba(31, 205, 255, 0.10) 100%), rgba(255, 255, 255, 0.50)',
  };

  return (
    <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-40 ${className}`}>
      <div
        style={navStyle}
        className="flex justify-center items-center gap-2 sm:gap-3 px-4 sm:px-9 py-1 sm:py-2 rounded-[43px] shadow-[0px_4px_12px_rgba(10,0,70,0.24)] outline outline-1 outline-offset-[-1px] outline-[rgba(31,205,255,0.20)] backdrop-blur-[8px]"
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab;
          return (
            <div
              key={tab}
              onClick={() => onTabChange(tab)}
              className="p-0.5 sm:p-1 cursor-pointer"
            >
              <div
                className={`text-center leading-4 tracking-tight transition-all duration-300 ease-in-out whitespace-nowrap ${
                  isActive
                    ? 'text-[#245EFF] text-sm sm:text-lg font-bold'
                    : 'text-[#0A0046] text-xs sm:text-base font-semibold'
                }`}
              >
                {tab}
              </div>
            </div>
          );
        })}

        {/* Logout Icon */}
        {showLogout && (
          <>
            <div className="w-px h-6 bg-[#0A0046]/20 mx-1" />
            <button
              onClick={handleLogout}
              className="p-1 sm:p-2 hover:opacity-70 transition-all group"
              title="Logout"
            >
              <img 
                src={LogoutIcon} 
                alt="Logout" 
                className="w-5 h-5 sm:w-6 sm:h-6 group-hover:brightness-0 group-hover:invert transition-all duration-300" 
              />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default FloatingNavBar;

// Tracking-specific bottom navigation bar
export const TrackingNavbar = ({ activeTab, onTabChange }) => {
  const tabs = [
    { id: 'booked', label: 'Booked' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'on-duty', label: 'On-duty' },
    { id: 'off-duty', label: 'Off-Duty' },
    { id: 'camera', label: 'Camera' },
    { id: 'map', label: 'Map' }
  ]

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg">
      <div className="flex items-center justify-center px-4 py-3">
        <div className="flex items-center gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}